import { useState, useEffect } from 'react';
import { fmtMoney, fmtNumber } from '@/lib/format';
import { SectionHeader, StatusBadge, FilterPills, PremiumTable, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { dashboardApi } from '@/services/dashboardApi';
import { motion } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Actividad comercial (solo brokers Premium) — datos reales del CDM (08/10/2026): altas que patrocinas,
  upgrades de esos socios y aportaciones donde eres N1. «Pendiente» = aún no ha pagado el 100 %;
  «Reserva» = plaza reservada, todavía no es alta.
  Hasta la licencia suiza no se habla de tokens en el panel: son «aportaciones» con su importe (09/10/2026).
  Ruta: /dashboard/actividad (la antigua /dashboard/tokens redirige aquí).
*/
const STATUSES = ['Todas', 'Completada', 'Pendiente', 'Reserva', 'Baja'];

// Inglés británico (05/11/2026). El filtro sigue enviando al servidor el estado en español.
const T = {
  es: {
    statusLabels: { Todas: 'Todas', Completada: 'Completada', Pendiente: 'Pendiente', Reserva: 'Reserva', Baja: 'Baja' },
    title: 'Actividad comercial',
    subtitle: 'Membresías, upgrades y aportaciones de los clientes que has traído al club',
    summary: (n, amount) => <>{n} operaciones · {amount} en curso o completadas</>,
    loadError: undefined,
    cols: ['Cliente', 'Operación', 'Importe', 'Pagado', 'Fecha', 'Estado'],
    emptyAll: 'Todavía no hay operaciones. Cuando un contacto tuyo se haga socio con tu enlace aparecerá aquí.',
    emptyFiltered: (f) => `No hay operaciones con estado «${f}».`,
    contribution: 'Aportación',
    deal: (name) => name,
    includedTitle: 'Membresía incluida en otra operación (sin precio propio)',
    included: 'Incluida',
    footer: 'Importes en euros. «Pendiente»: el socio aún no ha completado el pago. «Reserva»: plaza reservada que todavía no es alta.',
  },
  en: {
    statusLabels: { Todas: 'All', Completada: 'Completed', Pendiente: 'Pending', Reserva: 'Reserved', Baja: 'Cancelled' },
    title: 'Sales activity',
    subtitle: 'Memberships, upgrades and contributions from the clients you’ve brought to the club',
    summary: (n, amount) => <>{n} deals · {amount} in progress or completed</>,
    loadError: 'We couldn’t load your sales activity.',
    cols: ['Client', 'Deal', 'Amount', 'Paid', 'Date', 'Status'],
    emptyAll: 'No deals yet. When one of your contacts becomes a member through your link, it’ll appear here.',
    emptyFiltered: (f) => `No deals with the status ‘${f}’.`,
    contribution: 'Contribution',
    // «Membresía PRO» (lo compone el servidor) → «PRO membership»; los conceptos de pago del CDM se muestran tal cual
    deal: (name) => String(name || '').replace(/^Membresía\s*(.*)$/, (_, sku) => (sku ? `${sku} membership` : 'Membership')),
    includedTitle: 'Membership included in another deal (no separate price)',
    included: 'Included',
    footer: 'Amounts in euros. ‘Pending’: the member hasn’t completed payment yet. ‘Reserved’: a place has been reserved, but the membership isn’t active yet.',
  },
};

export default function DashboardActividad() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [sales, setSales] = useState([]);
  const [filter, setFilter] = useState('Todas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    dashboardApi.sales(filter === 'Todas' ? null : filter)
      .then((r) => { setSales(r); setError(null); })
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, [filter, attempt]);

  const total = sales.filter((s) => ['Completada', 'Pendiente'].includes(s.status)).reduce((a, s) => a + (s.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="dashboard-actividad">
      <SectionHeader title={t.title} subtitle={t.subtitle} />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        {/* Las pastillas muestran la etiqueta del idioma; el filtro guarda el valor en español que entiende el servidor */}
        <FilterPills
          options={STATUSES.map((v) => t.statusLabels[v])}
          active={t.statusLabels[filter]}
          onChange={(l) => setFilter(STATUSES.find((v) => t.statusLabels[v] === l) || 'Todas')}
        />
        {!loading && !error && sales.length > 0 && (
          <p className="text-xs text-gray-400">{t.summary(fmtNumber(sales.length), <span className="text-white font-medium">{fmtMoney(total, 'eur')}</span>)}</p>
        )}
      </div>
      {loading ? (
        <PageSkeleton cards={0} rows={6} />
      ) : error ? (
        <LoadError onRetry={() => setAttempt((n) => n + 1)} text={t.loadError} />
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <PremiumTable
            cols={t.cols}
            emptyText={filter === 'Todas' ? t.emptyAll : t.emptyFiltered(t.statusLabels[filter])}
            rows={sales.map((s) => [
              s.client || '—',
              s.kind === 'aportacion' ? <span className="text-[#c9a84c] font-medium">{t.contribution}</span> : t.deal(s.property_name),
              s.kind === 'membership' && !s.amount ? <span className="text-gray-400" title={t.includedTitle}>{t.included}</span> : fmtMoney(s.amount, s.currency || 'eur'),
              s.status === 'Reserva' ? '—' : fmtMoney(s.paid_amount ?? 0, s.currency || 'eur'),
              s.date ? `${s.date.slice(8, 10)}/${s.date.slice(5, 7)}/${s.date.slice(0, 4)}` : '—',
              <StatusBadge status={s.status} />,
            ])}
          />
          <p className="text-[11px] text-gray-500 mt-3">{t.footer}</p>
        </motion.div>
      )}
    </div>
  );
}
